import { useMutation, useQuery, useQueryClient, UseQueryOptions } from "@tanstack/react-query";
import { AjaxError } from "../../_core/api/types";
import {
  createPartnerUser,
  deletePartnerUser,
  getPartnerAdminStatus,
  getPartnerUsers,
  removePartnerUserEmailSuppression,
  resendPartnerUserWelcomeEmail
} from "../api";
import { CreatePartnerUserParams, GetPartnerUsersParams, GetPartnerUsersResp, PartnerAdmin } from "../api/types";

export const usePartnerUsers = (
  parameters: GetPartnerUsersParams, 
  options?: Omit<UseQueryOptions<GetPartnerUsersResp, AjaxError>, "queryKey" | "queryFn">
) => {
  return useQuery<GetPartnerUsersResp, AjaxError>({
    queryKey: ["partnerUsers", parameters],
    queryFn: () => getPartnerUsers(parameters),
    ...options,
  });
};

export const usePartnerAdminStatus = (
  options?: Omit<UseQueryOptions<PartnerAdmin, AjaxError>, "queryKey" | "queryFn">
) => {
  return useQuery<PartnerAdmin, AjaxError>({
    queryKey: ["partnerAdminStatus"],
    queryFn: getPartnerAdminStatus,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 3, // Retry up to 3 times on failure
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
    ...options,
  });
};

export const useCreatePartnerUser = () => {
  const queryClient = useQueryClient();
  
  return useMutation<string, AjaxError, CreatePartnerUserParams>({
    mutationFn: createPartnerUser,
    onSuccess: () => {
      // Invalidate and refetch partner users
      queryClient.invalidateQueries({ queryKey: ["partnerUsers"] });
    },
  });
};

export const useDeletePartnerUser = () => {
  const queryClient = useQueryClient();

  return useMutation<void, AjaxError, string>({
    mutationFn: deletePartnerUser,
    onSuccess: () => {
      // Invalidate and refetch partner users
      queryClient.invalidateQueries({ queryKey: ["partnerUsers"] });
    },
  });
};

export const useRemovePartnerUserEmailSuppression = () => {
  const queryClient = useQueryClient();

  return useMutation<void, AjaxError, string>({
    mutationFn: removePartnerUserEmailSuppression,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["partnerUsers"] });
    },
  });
};

export const useResendPartnerUserWelcomeEmail = () => {
  return useMutation<void, AjaxError, string>({
    mutationFn: resendPartnerUserWelcomeEmail,
  });
};